import type { FactsheetDetails, VersionHistory } from '../types';

/**
 * Safely retrieves the latest version history entry from a factsheet.
 * Returns undefined if no version history exists.
 */
export function getLatestVersionHistory(factsheet: FactsheetDetails): VersionHistory | undefined {
  if (factsheet.factsheetVersionHistory.length === 0) {
    return undefined;
  }
  return factsheet.factsheetVersionHistory[0];
}

/**
 * Retrieves a specific field from the latest version history.
 * Returns undefined if version history doesn't exist or field is empty.
 */
export function getVersionHistoryField<K extends keyof VersionHistory>(
  factsheet: FactsheetDetails,
  field: K
): VersionHistory[K] | undefined {
  const latestVersion = getLatestVersionHistory(factsheet);
  if (!latestVersion) {
    return undefined;
  }
  return latestVersion[field];
}

/**
 * Checks if a factsheet has a specific version history field with content.
 */
export function hasVersionHistoryField(
  factsheet: FactsheetDetails,
  field: keyof VersionHistory
): boolean {
  const value = getVersionHistoryField(factsheet, field);
  if (value == null) {
    return false;
  }
  if (typeof value === 'string') {
    return value.length > 0;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
}

/**
 * Parses a newline-separated text field from version history into an array of lines.
 * Returns empty array if the field doesn't exist.
 */
export function parseVersionHistoryText(
  factsheet: FactsheetDetails,
  field: 'qualitySpecification' | 'criteriaForInclusion' | 'calculationApproach'
): string[] {
  const text = getVersionHistoryField(factsheet, field);
  if (!text || typeof text !== 'string') {
    return [];
  }
  return text.split('\n');
}
